import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import API_BASE_URL from "../../../../Constants";
import DeviceAccordion from "../../../setup/components/Device List/components/DeviceAccordion";
import RenderFields from "./RenderFields";
import DeviceListTable from "./DeviceListTable";
import { selectNavHeight } from "../../../../redux/navbar/navHeightSlice";
import { useSelector } from "react-redux";

import {
  inspectionMainInfo,
  inspectionOtherInfo,
  inspectionCompanyInfo,
  inspectionDatesInfo,
} from "./InspectionModalConfig";

function splitFields(fieldsArray) {
  const midIndex = Math.ceil(fieldsArray.length / 2);
  return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
}

export default function ViewInspectionDeviceModal({
  open,
  onClose,
  refreshList,
  setNext,
  setPrev,
  numericInspectionId,
  inspectionNumber,
  selectedInspection,
}) {
  const [currentId, setCurrentId] = useState(numericInspectionId || null);

  const navHeight = useSelector(selectNavHeight);

  const [inspectionData, setInspectionData] = useState(null);
  const [linkedInspection, setLinkedInspection] = useState(null);
  const [buildingData, setBuildingData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // We fetch these to populate the drop-downs
  const [maintenanceCompanies, setMaintenanceCompanies] = useState([]);
  const [performingCompanies, setPerformingCompanies] = useState([]);
  const [witnessCompanies, setWitnessCompanies] = useState([]);

  const [mainLeft, mainRight] = splitFields(inspectionMainInfo);
  const [otherLeft, otherRight] = splitFields(inspectionOtherInfo);
  const [companyLeft, companyRight] = splitFields(inspectionCompanyInfo);
  const [datesLeft, datesRight] = splitFields(inspectionDatesInfo);
  // Remove these three lines:
  // const [maintenanceCompanies, setMaintenanceCompanies] = useState([]);
  // const [performingCompanies, setPerformingCompanies] = useState([]);
  // const [witnessCompanies, setWitnessCompanies] = useState([]);

  // Add this one line:
  const [allServiceCompanies, setAllServiceCompanies] = useState([]);

  useEffect(() => {
    if (numericInspectionId) {
      setCurrentId(numericInspectionId);
    }
  }, [numericInspectionId]);

  // 1) Fetch the Inspection
  useEffect(() => {
    if (!open || !currentId) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/inspections/id/${currentId}`, {
      credentials: "include",
    })
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`Could not fetch inspection. Status: ${resp.status}`);
        }
        return resp.json();
      })
      .then((data) => setInspectionData(data))
      .catch(() => setInspectionData(null))
      .finally(() => setLoading(false));
  }, [open, currentId]);

  // 2) Fetch the building
  const buildingId =
    inspectionData?.buildingId || selectedInspection?.buildingId || null;
  useEffect(() => {
    if (!open || !buildingId) return;
    fetch(`${API_BASE_URL}/api/setup/buildings/${buildingId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not fetch building. ${r.status}`);
        return r.json();
      })
      .then((bd) => setBuildingData(bd))
      .catch(() => setBuildingData(null));
  }, [open, buildingId]);

  // 3) Linked inspection
  useEffect(() => {
    if (!inspectionData?.linkedInspectionId) {
      setLinkedInspection(null);
      return;
    }
    const linkedId = inspectionData.linkedInspectionId;
    fetch(`${API_BASE_URL}/api/inspections/id/${linkedId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not fetch linked. ${r.status}`);
        return r.json();
      })
      .then((ld) => setLinkedInspection(ld))
      .catch(() => setLinkedInspection(null));
  }, [inspectionData]);

  // 4) Fetch all devices for building
  useEffect(() => {
    if (!open || !buildingId) return;
    fetch(`${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not fetch devices. ${r.status}`);
        return r.json();
      })
      .then((d) => setDevices(Array.isArray(d) ? d : []))
      .catch(() => setDevices([]));
  }, [open, buildingId]);

  // 5) Fetch ALL service companies for dropdowns using the general endpoint
  useEffect(() => {
    if (!open) return; // Only fetch when the modal is open

    const fetchAllCompanies = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inspections/service-companies`,
          {
            credentials: "include",
          }
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch service companies. Status: ${response.status}`
          );
        }
        const data = await response.json();
        // Set the single state variable with the fetched data
        setAllServiceCompanies(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load service companies:", error);
        setAllServiceCompanies([]); // Set to empty array on error
      }
    };

    fetchAllCompanies();
  }, [open]); // Re-fetch if the modal re-opens

  const handleClose = () => {
    if (!isEditMode) {
      onClose();
    }
  };
  const toggleExpand = () => setIsExpanded(!isExpanded);

  function validateFields() {
    const errs = {};
    // Example: if (!inspectionData.someField) errs.someField = "Required";
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function anyDeviceHasViolations() {
    return devices.some((d) => d.violationCount && d.violationCount > 0);
  }

  async function handleSaveChanges() {
    if (!validateFields()) {
      alert("Please fix errors before saving.");
      return;
    }
    let finalStatus = inspectionData?.status || "Inactive";
    if (anyDeviceHasViolations()) {
      finalStatus = "Active";
    }
    const updatedObj = { ...inspectionData, status: finalStatus };

    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/inspections/id/${currentId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(updatedObj),
        }
      );
      if (!resp.ok) {
        throw new Error(`Failed to update. Status: ${resp.status}`);
      }
      alert(`Saved successfully. Status => ${finalStatus}`);
      setIsEditMode(false);
      if (refreshList) refreshList();
    } catch {
      alert("Failed to save changes.");
    }
  }

  async function handleDelete() {
    if (!window.confirm("Delete inspection?")) return;
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/inspections/id/${currentId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!resp.ok) {
        throw new Error(`Delete failed: ${resp.status}`);
      }
      alert("Inspection deleted.");
      onClose();
      if (refreshList) refreshList();
    } catch {
      alert("Failed to delete.");
    }
  }

  // This is the main onChange for fields from RenderFields
  function handleFieldChange(e) {
    const { name, value } = e.target;
    setInspectionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  // Merge local + selected
  const mergedInsp = { ...selectedInspection, ...inspectionData };
  const displayedInspectionNumber =
    mergedInsp.inspectionNumber || inspectionNumber || `#${currentId ?? "N/A"}`;

  const finalBuildingSite =
    mergedInsp.buildingSite ||
    buildingData?.buildingReference ||
    "(No address)";

  // We keep the *actual IDs* here, not the name
  // so the <select> can set the numeric ID
  const combinedData = {
    inspectionNumber: mergedInsp.inspectionNumber,
    agencyName: mergedInsp.agencyName,
    status: mergedInsp.status,
    managementCompanyId: mergedInsp.managementCompanyId || "",
    test: mergedInsp.test || "",
    buildingSite: finalBuildingSite,
    customerName: mergedInsp.customerName || "N/A",

    // For the drop-downs, store the numeric ID
    maintenanceCoId: mergedInsp.maintenanceCoId || "",
    performingCoId: mergedInsp.performingCoId || "",
    witnessCoId: mergedInsp.witnessCoId || "",

    lastDate: mergedInsp.lastDate || "",
    nextDate: mergedInsp.nextDate || "",
    approvalDate: mergedInsp.approvalDate || "",
    inspYear: mergedInsp.inspYear || "",
    taskType: mergedInsp.taskType || "",
  };

  const linkedInspectionId = mergedInsp.linkedInspectionId;

  return (
    <>
      {open && !isEditMode && (
        <>
          <button
            onClick={setPrev}
            style={{
              position: "fixed",
              left: `calc(50% - ${isExpanded ? "45%" : "500px"} - 60px)`,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={setNext}
            style={{
              position: "fixed",
              right: `calc(50% - ${isExpanded ? "45%" : "500px"} - 60px)`,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            position: "absolute",
            top: navHeight,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 4,
            transition: "width 0.3s ease, height 0.3s ease",
            width: isExpanded ? "90%" : "1000px",
            height: `calc(100vh - ${navHeight + 8}px)`,
            overflow: "auto",
          },
        }}
        style={{ zIndex: 1400 }}
      >
        {/* HEADER */}
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isEditMode
                  ? `Edit Inspection # ${displayedInspectionNumber}`
                  : `View Inspection # ${displayedInspectionNumber}`}
              </h2>
            </div>
            <button
              onClick={toggleExpand}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          <div className="flex space-x-4 p-4">
            {isEditMode ? (
              <>
                <button
                  onClick={() => setIsEditMode(false)}
                  className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
                >
                  Save
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditMode(true)}
                className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* BODY */}
        <DialogContent dividers>
          {loading ? (
            <p>Loading inspection data...</p>
          ) : (
            <>
              {/* If there's a linked inspection, show the block */}
              {linkedInspectionId && inspectionData && linkedInspection && (
                <div className="mb-4">
                  <div className="p-3 mb-2 border border-yellow-400 rounded-md bg-yellow-50 text-yellow-900">
                    <p>
                      <strong>Current Inspection:</strong> #
                      {inspectionData.inspectionNumber} (
                      {inspectionData.test || "Unknown Category"})
                    </p>
                    <p className="text-sm">
                      Building: {inspectionData.buildingId}, Year:{" "}
                      {inspectionData.inspYear}
                    </p>
                  </div>

                  <div className="p-3 border border-blue-300 rounded-md bg-blue-50 text-blue-900">
                    <p>
                      <strong>Linked Inspection:</strong> #
                      {linkedInspection.inspectionNumber} (
                      {linkedInspection.test || "Unknown Category"})
                    </p>
                    <p className="text-sm">
                      Building: {linkedInspection.buildingId}, Year:{" "}
                      {linkedInspection.inspYear}
                    </p>
                    <button
                      className="underline text-blue-700 hover:text-blue-500 mt-1"
                      onClick={() => {
                        setInspectionData(null);
                        setLinkedInspection(null);
                        setCurrentId(linkedInspection.inspectionId);
                      }}
                    >
                      View Linked Inspection
                    </button>
                  </div>
                </div>
              )}

              {/* MAIN INFO */}
              <DeviceAccordion label="Main Inspection Information">
                <div
                  className={`w-full flex ${
                    isExpanded ? "flex-row" : "flex-col md:flex-row"
                  } gap-4`}
                >
                  <div className="w-1/2">
                    <RenderFields
                      fields={mainLeft}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-1/2">
                    <RenderFields
                      fields={mainRight}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>

              {/* OTHER INFO */}
              <DeviceAccordion label="Other Information">
                <div
                  className={`flex ${
                    isExpanded ? "flex-row" : "flex-col md:flex-row"
                  }`}
                >
                  <div className="w-1/2 pr-2">
                    <RenderFields
                      fields={otherLeft}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-1/2 pl-2">
                    <RenderFields
                      fields={otherRight}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>

              {/* COMPANY INFO */}
              <DeviceAccordion label="Company Information">
                <div
                  className={`flex ${
                    isExpanded ? "flex-row" : "flex-col md:flex-row"
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={companyLeft}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      // *** Pass the single list to all company props ***
                      maintenanceOptions={allServiceCompanies}
                      performingOptions={allServiceCompanies}
                      witnessOptions={allServiceCompanies}
                      // ************************************************

                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={companyRight}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      // *** Pass the single list to all company props ***
                      maintenanceOptions={allServiceCompanies}
                      performingOptions={allServiceCompanies}
                      witnessOptions={allServiceCompanies}
                      // ************************************************

                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>

              {/* DATES INFO */}
              {/* <DeviceAccordion label="Dates Information">
                <div
                  className={`flex ${
                    isExpanded ? "flex-row" : "flex-col md:flex-row"
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={datesLeft}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={datesRight}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion> */}

              {/* DEVICES */}
              <DeviceListTable
                inspectionId={currentId}
                buildingSite={finalBuildingSite}
                agencyId={mergedInsp.agencyId || null}
                categoryInspection={mergedInsp.test || ""}
                inspYear={mergedInsp.inspYear || ""}
                inspectionDate={mergedInsp.nextDate || ""}
                inspectionStatus={mergedInsp.status || ""}
                inspectionData={inspectionData || {}}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
